import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, TermsVersionRow } from '../api';
import { termsEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen, statusTone } from '../ui';

/**
 * Writing and publishing terms (item 191 — D-313).
 *
 * The screen is shaped by the one thing that is true of this data and not of
 * most: **once published, a version can never change**. So the draft is the only
 * editable thing on the page, publishing asks for confirmation naming the
 * version, and every older version is readable but not touchable.
 *
 * The published text is shown rather than hidden behind an expander, because the
 * question somebody comes here with is almost always "what does it currently
 * say".
 */
export default function TermsPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [note, setNote] = useState('');
  const [reading, setReading] = useState<string | null>(null);

  const terms = useQuery({ queryKey: ['terms'], queryFn: termsEndpoints.list });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['terms'] });
  const onError = (cause: ApiError) => setError(cause.message);

  const versions = terms.data?.versions ?? [];
  const draft = versions.find((version) => version.status === 'DRAFT') ?? null;
  const active = versions.find((version) => version.status === 'ACTIVE') ?? null;

  /*
   * The editor is filled from the draft once it arrives, and never again: a
   * refetch while somebody is typing must not overwrite what they have written.
   */
  const [loadedDraftId, setLoadedDraftId] = useState<string | null>(null);
  useEffect(() => {
    if (draft && draft.id !== loadedDraftId) {
      setBody(draft.body_md);
      setNote(draft.change_note ?? '');
      setLoadedDraftId(draft.id);
    }
  }, [draft, loadedDraftId]);

  const create = useMutation({
    mutationFn: () => termsEndpoints.draft({ document_code: 'TICKET_PURCHASE', body_md: body.trim() || active?.body_md || 'Terms go here.', change_note: note.trim() || null }),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError,
  });
  const save = useMutation({
    mutationFn: () => termsEndpoints.save(draft!.id, { body_md: body, change_note: note.trim() || null }),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError,
  });
  const publish = useMutation({
    mutationFn: () => termsEndpoints.activate(draft!.id),
    onSuccess: () => {
      setError(null);
      setLoadedDraftId(null);
      setBody('');
      setNote('');
      void refresh();
    },
    onError,
  });

  if (!can('settings.manage')) return <NotPermitted permission="settings.manage" />;
  const editable = can('terms.manage');

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Panel
        title="In force now"
        description={
          active
            ? `Version ${active.version}, published ${formatWhen(active.activated_at)}. Every order placed since then records agreement to this exact text, and it can never be edited.`
            : 'Nothing is published, so checkout asks nobody to agree to anything and records nothing. Publishing the first version is what turns that on.'
        }
      >
        {active ? (
          <pre className="max-h-72 overflow-y-auto px-5 py-4 text-xs whitespace-pre-wrap text-ink-700">{active.body_md}</pre>
        ) : (
          <p className="px-5 py-4 text-sm text-ink-500">No published terms.</p>
        )}
      </Panel>

      <Panel
        title={draft ? `Draft — version ${draft.version}` : 'Write a new version'}
        description={
          draft
            ? 'Editable until it is published. After that it is frozen: an agreement points at a version, so changing one would rewrite what everybody who agreed to it agreed to.'
            : 'A new version starts as a copy of what is in force, so a small change is a small edit.'
        }
        actions={
          editable && !draft ? (
            <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}>
              Start a draft
            </Button>
          ) : null
        }
      >
        {!draft ? (
          <p className="px-5 py-4 text-sm text-ink-500">No draft. Start one to change the terms.</p>
        ) : (
          <div className="space-y-3 px-5 py-4">
            <label htmlFor="terms-body" className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">
              The terms, as a customer will read them
            </label>
            <textarea
              id="terms-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              disabled={!editable}
              rows={18}
              className="w-full rounded border border-line bg-surface px-3 py-2 font-mono text-xs"
            />
            <label htmlFor="terms-note" className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">
              What changed (for staff, never shown to customers)
            </label>
            <input
              id="terms-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={!editable}
              className="w-full rounded border border-line bg-surface px-3 py-2 text-sm"
            />
            {editable ? (
              <div className="flex gap-2">
                <Button disabled={save.isPending} onClick={() => save.mutate()}>
                  {save.isPending ? 'Saving…' : 'Save draft'}
                </Button>
                <Button
                  variant="primary"
                  disabled={publish.isPending || body.trim().length === 0}
                  onClick={() => {
                    /*
                       Confirmed by naming the version, because publishing is the
                       irreversible step: from this moment the text is frozen and
                       every order records agreement to it.
                    */
                    if (window.confirm(`Publish version ${draft.version}? It becomes the terms every new order agrees to, and its text can never be changed afterward.`)) publish.mutate();
                  }}
                >
                  Publish version {draft.version}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-ink-400">Changing the terms needs terms.manage.</p>
            )}
          </div>
        )}
      </Panel>

      <Panel title="Every version" description="Readable forever, because an order somewhere points at each one.">
        <Table headers={['Version', 'Status', 'Published', 'What changed', '']}>
          {terms.isPending ? (
            <EmptyRow columns={5}>Loading…</EmptyRow>
          ) : versions.length === 0 ? (
            <EmptyRow columns={5}>No versions yet.</EmptyRow>
          ) : (
            versions.map((version) => (
              <TermsRow key={version.id} version={version} open={reading === version.id} onToggle={() => setReading(reading === version.id ? null : version.id)} />
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function TermsRow({ version, open, onToggle }: { version: TermsVersionRow; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="border-b border-line last:border-0">
        <td className="tabular px-5 py-3 text-sm text-ink-900">v{version.version}</td>
        <td className="px-5 py-3">
          <Badge tone={statusTone(version.status)}>{version.status.toLowerCase()}</Badge>
        </td>
        <td className="px-5 py-3 text-xs whitespace-nowrap text-ink-400">{version.activated_at ? formatWhen(version.activated_at) : 'never'}</td>
        <td className="px-5 py-3 text-sm text-ink-600">{version.change_note ?? '—'}</td>
        <td className="px-5 py-3 text-right">
          <Button onClick={onToggle}>{open ? 'Hide' : 'Read'}</Button>
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-line">
          <td colSpan={5} className="bg-page px-5 py-3">
            <pre className="max-h-72 overflow-y-auto text-xs whitespace-pre-wrap text-ink-700">{version.body_md}</pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}
